import * as THREE from 'three';
import { Mode } from 'types';

import { AnimationLoop } from './core/AnimationLoop';
import { CameraController } from './core/CameraController';
import { RaycasterManager } from './core/RaycasterManager';
import { SceneManager } from './core/SceneManager';
import { EventEmitter } from './helpers/EventEmitter';
import { BackgroundSystem } from './systems/BackgroundSystem';
import { BirthdaySystem } from './systems/BirthdaySystem';
import { EffectsSystem } from './systems/EffectsSystem';
import { GalaxyCoreSystem } from './systems/GalaxyCoreSystem';
import { MindLabel, MindLabelsSystem } from './systems/MindLabelsSystem';
import { PlanetsSystem } from './systems/PlanetsSystem';
import { PortalSystem } from './systems/PortalSystem';
import { SearchMarkerSystem } from './systems/SearchMarkerSystem';
import { SpaceshipSystem } from './systems/SpaceshipSystem';
import { StarsSystem } from './systems/StarsSystem';
import { EngineConfig, EngineEvents, EngineStar, ViewMode } from './types';

/**
 * Main engine class — assembles all subsystems.
 * React calls methods, subscribes to events. Engine manages all Three.js internally.
 *
 * Usage:
 *   const engine = new SpaceEngine(container, config);
 *   engine.setStars(stars);
 *   engine.on('star:click', (wallet, index) => { ... });
 *   engine.switchView('tiers');
 *   // later:
 *   engine.destroy();
 */
export class SpaceEngine extends EventEmitter<EngineEvents> {
  // Core
  private sceneManager: SceneManager;
  private animationLoop: AnimationLoop;
  private cameraController: CameraController;
  private raycaster: RaycasterManager;

  // Systems
  private stars: StarsSystem;
  private background: BackgroundSystem;
  private galaxyCore: GalaxyCoreSystem;
  private effects: EffectsSystem;
  private spaceship: SpaceshipSystem;
  private planets: PlanetsSystem;
  private portal: PortalSystem;
  private birthday: BirthdaySystem;
  private searchMarker = new SearchMarkerSystem();
  private mindLabels = new MindLabelsSystem();
  private currentMode: ViewMode = 'spiral';

  // Unsubscribe functions
  private unsubTick: (() => void) | null = null;
  private unsubPostRender: (() => void) | null = null;

  // Star tracking for target frame
  private starTrackingRaf = 0;

  constructor(
    private container: HTMLDivElement,
    private config: EngineConfig
  ) {
    super();

    // --- Core ---
    this.sceneManager = new SceneManager(container, config);
    this.animationLoop = new AnimationLoop(this.sceneManager);

    const { scene, camera, renderer, controls, portalScene } = this.sceneManager;

    this.cameraController = new CameraController(camera, controls, renderer);

    // --- Systems ---
    this.stars = new StarsSystem(scene, config);
    this.background = new BackgroundSystem(scene, config);
    this.galaxyCore = new GalaxyCoreSystem(scene, camera, renderer);
    this.effects = new EffectsSystem(scene, camera, this.stars);
    this.spaceship = new SpaceshipSystem(container, scene, camera, renderer, controls);
    this.planets = new PlanetsSystem(scene, camera, controls);
    this.portal = new PortalSystem(container, camera, controls, scene, renderer, portalScene);
    this.birthday = new BirthdaySystem(scene);

    // --- Raycaster ---
    this.raycaster = new RaycasterManager(container, camera, {
      onStarClick: (wallet, index, x, y) => this.emit('star:click', wallet, index, x, y),
      onShipClick: () => this.emit('ship:click'),
      onPortalClick: (distance) => this.emit('portal:click', distance),
      onEmptyClick: () => this.emit('empty:click'),
    });

    // --- Wire callbacks ---
    this.cameraController.setMorphCallbacks(
      () => this.birthday.startMorphToFace(),
      () => this.birthday.startMorphToText()
    );

    this.planets.setSwapCallbacks(
      () => this.emit('swap:enter'),
      () => this.emit('swap:exit')
    );

    this.portal.setCallbacks(
      () => this.emit('portal:arrived'),
      () => this.emit('portal:returned'),
      (_atPortal: boolean) => {
        // The React side can listen to portal:arrived / portal:returned
      },
      () => {
        // Wormhole travel finished — smoothly return camera and reset mode
        // so the Galaxy UI (ViewSelector, TraderDetailPanel, EventFeed, etc.)
        // becomes active again and the "Galaxy" menu button is highlighted.
        this.cameraController.returnToMainView();
        this.emit('portal:returned');
      }
    );

    // --- Animation loop ---
    this.unsubTick = this.animationLoop.onTick((dt) => this.onTick(dt));
    this.unsubPostRender = this.animationLoop.onPostRender(() => this.onPostRender());

    this.animationLoop.start();
  }

  // ============================
  // Public API: Data
  // ============================

  setStars(stars: EngineStar[]): void {
    this.stars.setStars(stars);
    this.raycaster.setPoints(this.stars.getPoints());
    this.raycaster.setWallets(stars.map((s) => s.wallet));

    // Pass star material to AnimationLoop for depth-restore pass (depthCoreOnly)
    const material = this.stars.getMaterial();
    if (material) {
      this.animationLoop.setStarMaterial(material);
    }

    // Stars are now in the scene — refresh logo envMap (initial capture was dark)
    this.galaxyCore.refreshEnvMap();
  }

  // ============================
  // Public API: View Modes
  // ============================

  switchView(mode: ViewMode): void {
    this.currentMode = mode;
    this.mindLabels.setVisible(mode === 'mind');
    this.background.bakeGroupRotation();
    this.stars.switchView(mode);
    this.background.switchView(mode);
  }

  setPilotMode(active: boolean): void {
    this.stars.setSmoothRotation(active);
    this.background.setSmoothRotation(active);
  }

  getViewTransitionProgress(): number {
    return this.stars.getTransitionProgress();
  }

  isViewTransitioning(): boolean {
    return this.stars.isTransitioning();
  }

  // ============================
  // Public API: Spaceship
  // ============================

  setShipMode(mode: Mode, returnCamera = true): void {
    const wasFollowing = !this.spaceship.isOrbit();
    this.spaceship.setMode(mode);
    this.raycaster.setFollowMode(mode !== 'main');
    if (mode === 'main' && wasFollowing && returnCamera) {
      this.cameraController.returnToMainView();
    }
  }

  resetShipMode(returnCamera = true): void {
    this.setShipMode('main', returnCamera);
  }

  setScanCompleteCallback(cb: (wallets: { address: string; roi: number }[]) => void): void {
    this.spaceship.setScanCompleteCallback(cb);
  }

  /**
   * Provide the pool of real wallets the autopilot scan should pick
   * from when it emits "discovered wallets" events. Pass the list
   * loaded from /api/whitelist; an empty list makes the spaceship fall
   * back to synthetic random hex addresses, which is the right
   * behaviour while data loads or if the API call fails.
   */
  setKnownWallets(wallets: { address: string; roi: number }[]): void {
    this.spaceship.setKnownWallets(wallets);
  }

  // ============================
  // Public API: Camera
  // ============================

  /**
   * Track a star's screen position each frame for `duration` ms.
   * Calls onUpdate(x, y) every frame, onDone() when done.
   * Matches original: target frame follows star as it rotates with the galaxy.
   */
  trackStar(
    index: number,
    duration: number,
    onUpdate: (x: number, y: number) => void,
    onDone: () => void
  ): void {
    this.stopStarTracking();

    const points = this.stars.getPoints();
    if (!points) {
      setTimeout(onDone, duration);
      return;
    }

    const positions = points.geometry.attributes.position.array as Float32Array;
    const { camera, renderer } = this.sceneManager;
    const startTime = performance.now();
    const tmp = new THREE.Vector3();

    const tick = () => {
      if (performance.now() - startTime >= duration) {
        this.starTrackingRaf = 0;
        onDone();
        return;
      }

      tmp.set(positions[index * 3], positions[index * 3 + 1], positions[index * 3 + 2]);
      points.localToWorld(tmp);
      tmp.project(camera);

      const rect = renderer.domElement.getBoundingClientRect();
      const x = (tmp.x * 0.5 + 0.5) * rect.width + rect.left;
      const y = (-tmp.y * 0.5 + 0.5) * rect.height + rect.top;

      onUpdate(x, y);
      this.starTrackingRaf = requestAnimationFrame(tick);
    };

    this.starTrackingRaf = requestAnimationFrame(tick);
  }

  stopStarTracking(): void {
    if (this.starTrackingRaf) {
      cancelAnimationFrame(this.starTrackingRaf);
      this.starTrackingRaf = 0;
    }
  }

  /**
   * Fly the camera to a star by index. Reads the star's CURRENT position
   * from geometry (view-mode aware) and converts to world space, so it
   * works in every view mode including rotated groups.
   */
  flyToStar(index: number, distance: number = 26): void {
    const points = this.stars.getPoints();
    if (!points || index < 0) return;

    const positions = points.geometry.attributes.position.array as Float32Array;
    if (index * 3 + 2 >= positions.length) return;

    const target = new THREE.Vector3(
      positions[index * 3],
      positions[index * 3 + 1],
      positions[index * 3 + 2]
    );
    points.localToWorld(target);
    this.cameraController.flyToPoint(target, distance);
  }

  /**
   * Highlight a subset of stars (dim everything else). Pass null to clear.
   */
  setHighlight(indices: number[] | null): void {
    this.stars.setHighlight(indices);
  }

  /**
   * Place the pulsing search marker at map-space coords (same space the view
   * calculators emit). Attached to the stars group, so it rotates with it.
   */
  setSearchMarker(pos: { x: number; y: number; z: number }): void {
    const points = this.stars.getPoints();
    if (!points) return;
    this.searchMarker.set(points, pos);
  }

  clearSearchMarker(): void {
    this.searchMarker.clear();
  }

  /**
   * Feed constellation labels (name + centroid in map space). Attached to
   * the stars group; shown only while the 'mind' view is active.
   */
  setMindLabels(labels: MindLabel[]): void {
    const points = this.stars.getPoints();
    if (!points) return;
    const anisotropy = this.sceneManager.renderer.capabilities.getMaxAnisotropy();
    this.mindLabels.setLabels(points, labels, anisotropy);
    this.mindLabels.setVisible(this.currentMode === 'mind');
  }

  /**
   * Fly the camera to a point given in map-space (stars-local) coordinates.
   */
  flyToMapPoint(pos: { x: number; y: number; z: number }, distance: number = 30): void {
    const points = this.stars.getPoints();
    if (!points) return;
    const target = new THREE.Vector3(pos.x, pos.y, pos.z);
    points.localToWorld(target);
    this.cameraController.flyToPoint(target, distance);
  }

  // ============================
  // Public API: Alien View
  // ============================

  enterAlienView(): void {
    this.cameraController.enterAlienView();
  }

  exitAlienView(): void {
    this.cameraController.exitAlienView();
  }

  // ============================
  // Public API: Portal
  // ============================

  flyToPortal(): void {
    this.portal.flyToPortal();
  }

  flyBack(): void {
    this.portal.flyBack();
  }

  startWormholeTravel(): void {
    this.portal.startTravel();
  }

  stopWormholeTravel(immediate = false): void {
    this.portal.stopTravel(immediate);
  }

  // ============================
  // Public API: Swap
  // ============================

  enterSwapMode(): void {
    if (this.spaceship.getShipGroup()) {
      this.resetShipMode(false);
    }
    this.planets.enterSwapMode();
  }

  exitSwapMode(): void {
    this.planets.exitSwapMode();
  }

  // ============================
  // Public API: Effects
  // ============================

  triggerEffect(traderIndex: number, type: 'supernova' | 'collapse', intensity: number): void {
    this.effects.triggerEffect(traderIndex, type, intensity);
  }

  // ============================
  // Private: Animation loop
  // ============================

  private shipPassedToRaycaster = false;

  private onTick(dt: number): void {
    if (!this.shipPassedToRaycaster) {
      const ship = this.spaceship.getShipGroup();
      if (ship) {
        this.raycaster.setShip(ship);
        this.shipPassedToRaycaster = true;
      }
    }

    // Stars
    this.stars.animate(dt);

    // Background: sync nebula rotation with stars, then animate
    this.background.setRotationY(this.stars.getRotationY());
    this.background.animate(dt);

    // Galaxy core (BNB logo)
    this.galaxyCore.animate(dt);

    // Spaceship
    this.spaceship.tick(dt);

    // Planets
    this.planets.tick(dt);

    // Portal
    this.portal.tick(dt);

    // Birthday
    this.birthday.tick(dt);

    // Effects
    this.effects.animate(dt, this.stars.getStars());

    // Search marker pulse
    this.searchMarker.tick(dt);

    // Constellation labels fade
    this.mindLabels.tick(dt, this.sceneManager.camera);
  }

  private onPostRender(): void {
    // Galaxy core logo rendered after bloom
    this.galaxyCore.render();
  }

  // ============================
  // Lifecycle
  // ============================

  destroy(): void {
    this.stopStarTracking();
    this.animationLoop.stop();

    this.unsubTick?.();
    this.unsubPostRender?.();

    // Destroy systems
    this.raycaster.destroy();
    this.cameraController.destroy();
    this.searchMarker.destroy();
    this.mindLabels.destroy();
    this.stars.destroy();
    this.background.destroy();
    this.galaxyCore.destroy();
    this.effects.destroy();
    this.spaceship.destroy();
    this.planets.destroy();
    this.portal.destroy();
    this.birthday.destroy();

    // Destroy core
    this.sceneManager.destroy();

    this.removeAllListeners();
  }
}
